// src/components/common/Modal.tsx

import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
  maxWidth?: string;
};

export const Modal = ({ isOpen, onClose, title, children, width = "90vw", maxWidth = "600px" }: Props) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9999] overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm will-change-[opacity]"
            onClick={onClose}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              {/* Modal Content */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ 
                  type: "spring",
                  duration: 0.5,
                  bounce: 0.3,
                  stiffness: 300,
                  damping: 30
                }}
                className="relative transform overflow-hidden rounded-lg bg-white text-right shadow-xl will-change-transform"
                style={{ 
                  width: width,
                  maxWidth: maxWidth,
                  margin: '20px auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute left-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none z-10"
                >
                  <X size={20} />
                </button>

                {/* Title */}
                {title && (
                  <div className="bg-gray-50 px-6 py-4 border-b">
                    <h3 
                      className="text-lg font-medium text-gray-900" 
                      id="modal-title"
                    >
                      {title}
                    </h3>
                  </div>
                )}

                {/* Content */}
                <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
                  {children}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
